import { useMemo } from 'react';
import { FileText, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import MetricsCard from './MetricsCard';
import { IUFTableData, IStoreTableData } from '../utils';

interface MetricsGridProps {
  chartData: any[];
  ufData: IUFTableData[];
  storeData: IStoreTableData[];
  isFetching: boolean;
}

export default function MetricsGrid({ ufData, isFetching }: MetricsGridProps) {
  const metrics = useMemo(() => {
    if (isFetching || !ufData.length) {
      return {
        totalNFCe: 0,
        successRate: 0,
        totalAlerts: 0,
        pendingCount: 0
      };
    }

    const totals = ufData.reduce((acc, item) => ({
      total: acc.total + item.Total,
      success: acc.success + item.Sucesso,
      pending: acc.pending + item.Pendente,
      contingency: acc.contingency + item.Contingência,
      cancelled: acc.cancelled + item.Cancelado
    }), { total: 0, success: 0, pending: 0, contingency: 0, cancelled: 0 });

    const successRate = totals.total > 0 ? (totals.success / totals.total) * 100 : 0;
    const totalAlerts = totals.contingency + totals.cancelled;

    return {
      totalNFCe: totals.total,
      successRate: Math.round(successRate * 10) / 10,
      totalAlerts,
      pendingCount: totals.pending
    };
  }, [ufData, isFetching]);

  if (isFetching) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 bg-gray-100 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <MetricsCard
        title="Total de NFCe"
        value={metrics.totalNFCe}
        icon={<FileText className="h-4 w-4" />}
        status="info"
        subtitle="Notas processadas"
      />

      <MetricsCard
        title="Taxa de Sucesso"
        value={`${metrics.successRate}%`}
        icon={<CheckCircle className="h-4 w-4" />}
        status={metrics.successRate >= 95 ? "success" : metrics.successRate >= 80 ? "warning" : "error"}
        trend={metrics.successRate >= 90 ? "+2.1%" : "-1.5%"}
        trendDirection={metrics.successRate >= 90 ? "up" : "down"}
        subtitle="NFCe aprovadas"
      />

      <MetricsCard
        title="Alertas"
        value={metrics.totalAlerts}
        icon={<AlertTriangle className="h-4 w-4" />}
        status={metrics.totalAlerts === 0 ? "success" : metrics.totalAlerts < 10 ? "warning" : "error"}
        subtitle="Contingência + Canceladas"
      />

      <MetricsCard
        title="Pendentes"
        value={metrics.pendingCount}
        icon={<Clock className="h-4 w-4" />}
        status={metrics.pendingCount === 0 ? "success" : "warning"}
        subtitle="Aguardando processamento"
      />
    </div>
  );
}